/**
 * Reconnect and policy-refusal state for the WebSocket transport.
 *
 * Network failures ride socket.io's bounded back-off; a policy-level
 * authz_denied refusal is latched here instead, because retrying with the
 * same credential can never succeed. The latch survives until disconnect()
 * so providers that mount after the refusal still observe awaiting-grant.
 */
export class ReconnectPolicy {
  readonly maxAttempts = 5
  /** Base socket.io reconnection delay in ms. */
  readonly baseDelayMs = 1000

  private attempts = 0
  /** True after a policy-level authz_denied refusal — reconnection is suppressed. */
  private authzDenied = false
  private authzDeniedCallbacks = new Set<() => void>()

  get currentAttempts(): number {
    return this.attempts
  }

  recordAttempt(attempt: number): void {
    this.attempts = attempt
  }

  resetAttempts(): void {
    this.attempts = 0
  }

  exhausted(): boolean {
    return this.attempts >= this.maxAttempts
  }

  /** Latch the authz_denied refusal and notify every subscriber once. */
  markAuthzDenied(): void {
    this.authzDenied = true
    this.authzDeniedCallbacks.forEach((cb) => cb())
  }

  isAuthzDenied(): boolean {
    return this.authzDenied
  }

  /**
   * Register a callback invoked once when an authz_denied refusal is
   * received. Fires immediately if already denied (e.g. the subscriber
   * mounts after the refusal). Returns a cleanup function.
   */
  onAuthzDenied(cb: () => void): () => void {
    this.authzDeniedCallbacks.add(cb)
    if (this.authzDenied) cb()
    return () => {
      this.authzDeniedCallbacks.delete(cb)
    }
  }

  /** Full reset on disconnect — clears attempts, the latch, and subscribers. */
  reset(): void {
    this.attempts = 0
    this.authzDenied = false
    this.authzDeniedCallbacks.clear()
  }
}
